/**
 * Brief Dock Context — "the Daily Brief docks into / out of the header bell"
 *
 * Mirrors ChatDockContext's shared-element morph, but for the Daily Brief: the
 * header BELL is the docked (closed) state and the Brief panel is the open
 * state. A single layoutId (BRIEF_DOCK_LAYOUT_ID) is shared between the bell
 * (HeaderBriefLauncher, mounted ONLY when closed) and the flight surface
 * (BriefDockFlight, mounted during the transition) inside MainLayout's
 * <LayoutGroup>, so framer-motion morphs the bell INTO the panel on open and
 * the panel back into the bell on close — the bell literally grows/shrinks,
 * exactly like the chat-dock launcher.
 *
 * State machine (mirrors ChatDock's docked↔open, here closed↔open):
 *
 *   'closed' --openBrief()--> 'undocking' --(flight lands ~230ms)--> 'open'
 *   'open'   --closeBrief()--> 'docking'   --(content fade ~70ms)--> 'closed'
 *
 * - On OPEN ('closed'→'undocking'): the bell unmounts and the flight mounts at
 *   the panel rect — framer FLIPs the shared layoutId from the bell slot to the
 *   panel (GROW). At 'open' the flight unmounts and the real panel reveals.
 * - On CLOSE ('open'→'docking'): the panel content fades and the flight mounts
 *   at the panel rect; at 'closed' the flight unmounts and the bell mounts —
 *   framer FLIPs the shared layoutId from the panel into the bell slot (SHRINK).
 * - The morph only runs when a dockable DESKTOP placement is live
 *   (setPlacementActive(true): solo companion OR module-open split, ≥768px).
 *   The mobile modal (≤767px) + prefers-reduced-motion skip it (instant swap).
 * - There is NO persistence — the Brief defaults closed every load.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMediaQuery } from '@/common/hooks/useMediaQuery';
import {
  CHAT_DOCK_CONTENT_FADE_MS,
  CHAT_DOCK_UNDOCK_LAND_MS,
  type DockRect,
} from '@/common/motion/chatDockMotion';

export type BriefDockState = 'closed' | 'docking' | 'open' | 'undocking';

interface BriefDockContextValue {
  dockState: BriefDockState;
  /** True while a dockable desktop placement is live (solo OR split, ≥768px). */
  isPlacementActive: boolean;
  /** Raised by a user close; consumed by HeaderBriefLauncher (landing FX + focus). */
  pendingLandingFx: boolean;
  /** Raised by a user open; consumed by the panel (focus the close button on arrival). */
  pendingOpenFocus: boolean;
  /**
   * Raised in the same commit the open flight lands ('undocking' → 'open'), so
   * the panel can wear `.brief-dock-landing` from the very first 'open' frame
   * (a frame-late class would let the default entrance keyframe flash first).
   */
  pendingLandingReveal: boolean;
  /**
   * Resting rect of the Brief panel for the current flight (set synchronously
   * when a transition starts so the flight surface mounts in the same commit the
   * bell/panel swaps — the shared-element FLIP depends on that). framer remembers
   * the bell's rect from the unmounting launcher, so only this one is needed.
   */
  flightRect: DockRect | null;
  /** User clicked the bell while closed: animate bell → panel. */
  openBrief: () => void;
  /** User closed the panel (X / bell / Escape): animate panel → bell. */
  closeBrief: () => void;
  /**
   * MainLayout registers a synchronous "where is the panel's resting rect?"
   * measurer here; both transitions call it before flipping state. A null rect
   * (placement inactive / first-paint race) falls back to an instant swap.
   */
  registerPanelRectProvider: (provider: (() => DockRect | null) | null) => void;
  /** Called by the flight surface when the open flight lands. */
  finishOpen: () => void;
  ackLandingFx: () => void;
  ackOpenFocus: () => void;
  ackLandingReveal: () => void;
  /**
   * True while a dockable desktop placement (solo OR split) is live. Gates the
   * morph vs an instant swap. Flipping it false synchronously settles any
   * in-flight transition so a mid-flight placement change can't orphan a timer.
   */
  setPlacementActive: (active: boolean) => void;
}

const BriefDockContext = createContext<BriefDockContextValue | undefined>(undefined);

export function BriefDockProvider({ children }: { children: ReactNode }) {
  const [dockState, setDockState] = useState<BriefDockState>('closed');
  const [isPlacementActive, setIsPlacementActiveState] = useState(false);
  const [pendingLandingFx, setPendingLandingFx] = useState(false);
  const [pendingOpenFocus, setPendingOpenFocus] = useState(false);
  const [pendingLandingReveal, setPendingLandingReveal] = useState(false);
  const [flightRect, setFlightRect] = useState<DockRect | null>(null);
  const panelRectProviderRef = useRef<(() => DockRect | null) | null>(null);
  const placementActiveRef = useRef(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Eagerly-updated mirror so the transition guards are airtight against
  // double-clicks landing in the same tick (state updates are async).
  const dockStateRef = useRef(dockState);
  const prefersReducedMotionRef = useRef(prefersReducedMotion);
  useEffect(() => {
    prefersReducedMotionRef.current = prefersReducedMotion;
  }, [prefersReducedMotion]);

  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearSettleTimer, [clearSettleTimer]);

  const transition = useCallback((next: BriefDockState) => {
    dockStateRef.current = next;
    setDockState(next);
  }, []);

  const openBrief = useCallback(() => {
    if (dockStateRef.current !== 'closed') return;
    setPendingOpenFocus(true);
    setPendingLandingReveal(false);
    clearSettleTimer();
    // Measure the landing rect BEFORE flipping state so the flight surface mounts
    // in the same commit the bell unmounts — framer-motion needs the bell's
    // layout snapshot alive for the shared-element FLIP. Skip the flight under
    // reduced motion, when no dockable placement is live, or when the rect can't
    // be measured — open instantly rather than entering a phase that never lands.
    const canFly =
      !prefersReducedMotionRef.current && placementActiveRef.current;
    const rect = canFly ? panelRectProviderRef.current?.() ?? null : null;
    setFlightRect(rect);
    if (rect === null) {
      transition('open');
      return;
    }
    transition('undocking');
    // Deliberately the PRIMARY landing trigger, not just a fallback: framer's
    // rest detection (onLayoutAnimationComplete → finishOpen) lags the
    // visually-settled box by 100-300ms, which reads as the glass "lingering"
    // before the real content appears. Whichever fires first wins.
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      setPendingLandingReveal(true);
      transition('open');
      // Reuses the chat dock's 230ms land clock so the Brief content reveal
      // starts at the same moment relative to its (now identical) spring.
    }, CHAT_DOCK_UNDOCK_LAND_MS);
  }, [clearSettleTimer, transition]);

  const closeBrief = useCallback(() => {
    if (dockStateRef.current !== 'open') return;
    setPendingLandingFx(true);
    setPendingLandingReveal(false);
    clearSettleTimer();
    // The panel is mounted (open), so the provider measures its live rect — the
    // flight mounts there and shrinks into the bell as 'closed' mounts it.
    const canFly =
      !prefersReducedMotionRef.current && placementActiveRef.current;
    const rect = canFly ? panelRectProviderRef.current?.() ?? null : null;
    setFlightRect(rect);
    if (rect === null) {
      transition('closed');
      return;
    }
    transition('docking');
    // Hold 'docking' for the content fade; at 'closed' the flight unmounts and
    // the bell mounts — framer FLIPs the shared layoutId from the panel rect
    // into the bell slot (the shrink). The CSS content fade runs early underneath.
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      transition('closed');
    }, CHAT_DOCK_CONTENT_FADE_MS);
  }, [clearSettleTimer, transition]);

  const registerPanelRectProvider = useCallback(
    (provider: (() => DockRect | null) | null) => {
      panelRectProviderRef.current = provider;
    },
    [],
  );

  const finishOpen = useCallback(() => {
    if (dockStateRef.current !== 'undocking') return;
    clearSettleTimer();
    // Same-batch with the state flip: the panel must wear .brief-dock-landing on
    // the very first 'open' frame or the default entrance keyframe flashes.
    setPendingLandingReveal(true);
    transition('open');
  }, [clearSettleTimer, transition]);

  const ackLandingFx = useCallback(() => setPendingLandingFx(false), []);
  const ackOpenFocus = useCallback(() => setPendingOpenFocus(false), []);
  const ackLandingReveal = useCallback(() => setPendingLandingReveal(false), []);

  const setPlacementActive = useCallback((active: boolean) => {
    setIsPlacementActiveState(active);
    placementActiveRef.current = active;
    // Leaving a dockable placement orphans any pending one-shot effects + the
    // settle timer — the panel host can unmount mid-flight (module opened,
    // viewport narrowed) and the timer would later fire into a void. Settle any
    // in-flight transition synchronously instead.
    if (!active) {
      setPendingLandingFx(false);
      setPendingOpenFocus(false);
      setPendingLandingReveal(false);
      if (dockStateRef.current === 'docking' || dockStateRef.current === 'undocking') {
        clearSettleTimer();
        transition(dockStateRef.current === 'docking' ? 'closed' : 'open');
      }
    }
  }, [clearSettleTimer, transition]);

  const value = useMemo<BriefDockContextValue>(
    () => ({
      dockState,
      isPlacementActive,
      pendingLandingFx,
      pendingOpenFocus,
      pendingLandingReveal,
      flightRect,
      openBrief,
      closeBrief,
      registerPanelRectProvider,
      finishOpen,
      ackLandingFx,
      ackOpenFocus,
      ackLandingReveal,
      setPlacementActive,
    }),
    [
      dockState,
      isPlacementActive,
      pendingLandingFx,
      pendingOpenFocus,
      pendingLandingReveal,
      flightRect,
      openBrief,
      closeBrief,
      registerPanelRectProvider,
      finishOpen,
      ackLandingFx,
      ackOpenFocus,
      ackLandingReveal,
      setPlacementActive,
    ],
  );

  return <BriefDockContext.Provider value={value}>{children}</BriefDockContext.Provider>;
}

export function useBriefDock() {
  const context = useContext(BriefDockContext);
  if (!context) {
    throw new Error('useBriefDock must be used within a BriefDockProvider');
  }
  return context;
}
